import {useState} from 'react';
import {
  Box,
  Typography,
  styled,
  useTheme,
  Card as MuiCard,
  Grid,
} from '@mui/material';
import {UserProfileCardProps} from '../type';
import Avatar from '@jobseeker/lotus/components/Avatar';
import Accordion, {AccordionSummary} from '@jobseeker/lotus/elements/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import CardOverlay from './overlay';
import ProgressBar from '@jobseeker/lotus/elements/ProgressBar';
import {ChevronRight} from '@jobseeker/lotus/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import LinkSection from './LinkSection';
import BottomSheet from '@jobseeker/lotus/elements/BottomSheet';
import {getOldLink, truncateString} from '@jobseeker/lotus/utils';
import {ButtonV2 as Button} from '@jobseeker/lotus/elements';
import Pro from '@jobseeker/lotus/elements/Pro';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';

const Card = styled(MuiCard)<any>(({theme}: any) => ({
  borderRadius: theme.spacing(2),
  position: 'relative',
  // padding: theme.spacing(2),
}));

const Component = ({mode = 'USER', ...props}: UserProfileCardProps) => {
  const theme = useTheme();
  const [expand, setExpand] = useState(false);
  // const sheetRef = useRef<BottomSheetRef>();

  const isLarge = useIsLargeDevice();
  const toggleAcordion = () => {
    setExpand(prev => !prev);
  };

  return (
    <>
      <Card sx={{boxShadow: theme.shadows[1]}}>
        <CardOverlay
          sx={
            mode == 'RECRUITER' && {
              background: 'linear-gradient(90deg, #333333 2.65%, #999999 100%)',
            }
          }
        />
        <Box sx={{p: theme.spacing(2, 2, mode === 'USER' ? 0.5 : 2, 2)}}>
          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mt: 7.5,
              overflow: 'visible',
              // boxShadow: 2,
              boxShadow: '5px 9px 20px 3px rgba(19, 21, 23, 0.04)',
              textAlign: 'center',
              pb: 1,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                zIndex: 2,
                right: 0,
                left: 0,
                display: 'flex',
                justifyContent: 'center',
                mt: '-40px',
              }}
            >
              <Box
                sx={{
                  p: 0.6,
                  background: '#ffffff22',
                  borderRadius: '100%',
                  cursor:
                    mode == 'USER'
                      ? !props.profileImgUrl
                        ? 'pointer'
                        : 'unset'
                      : 'unset',
                }}
              >
                <Avatar
                  imgUrl={props.profileImgUrl}
                  showFallbackIcon={mode == 'USER'}
                  onClick={
                    mode == 'USER'
                      ? () => {
                          openUrl(
                            getOldLink(
                              '/registration/addPersonalDetails?profiletype=1&ref=profilewdg'
                            ),
                            '_self'
                          );
                        }
                      : undefined
                  }
                  sx={{width: 60, height: 60}}
                />
              </Box>
            </Box>
            <Box sx={{px: 1, pt: 4, pb: 1, width: '100%'}}>
              <Typography
                color="secondary"
                variant="subtitle2"
                fontSize={theme.typography.pxToRem(14)}
                fontWeight={600}
                lineHeight={'21px'}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                {typeof props.name === 'string'
                  ? truncateString(props.name, 18)
                  : props.name}
                {props?.isProMember && (
                  <Box display="block" ml={theme.spacing(0.5)}>
                    <Pro bgColor="linear-gradient(180deg, #FFB743 0%, #F99914 100%)" />
                  </Box>
                )}
              </Typography>
              {props.email && (
                <Typography
                  color="secondary"
                  variant="body3"
                  lineHeight={'18px'}
                  sx={{
                    wordWrap: 'break-word',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {truncateString(props.email, 25)}
                </Typography>
              )}
              {props.subtitle && (
                <Typography
                  color="secondary"
                  variant="body3"
                  lineHeight={'15px'}
                  component={mode === 'RECRUITER' ? 'h1' : 'p'}
                  px={{xxs: 1, md: 0}}
                  // sx={{
                  //   wordWrap: 'break-word',
                  //   textOverflow: 'ellipsis',
                  // }}
                >
                  {props.subtitle}
                </Typography>
              )}
            </Box>
            {mode == 'USER' && (
              <>
                {!props?.isProMember && (
                  <Button
                    sx={{
                      display: {xxs: 'none', md: 'block'},
                      fontSize: theme.typography.pxToRem(10),
                      fontWeight: 500,
                      lineHeight: '15px',
                      letterSpacing: '0.4px',
                      padding: '4px 16px',
                      borderRadius: 4,
                      boxShadow: '5px 9px 20px 3px rgba(19, 21, 23, 0.04)',
                      borderColor: '#FFB743 !important',
                      ':hover': {
                        backgroundColor: '#fff',
                      },
                    }}
                    variant="outlined"
                    size="large"
                    style={{
                      color: '#FFB743',
                    }}
                    onClick={event => {
                      event?.preventDefault();
                      openUrl(props?.proMemberUrl || '', '_self');
                    }}
                  >
                    Upgrade to PRO
                  </Button>
                )}
              </>
            )}
          </Card>
          {mode == 'USER' && (
            <Box mt={1.5}>
              <ProgressBar
                value={props?.profileProgress ? props?.profileProgress : 0}
                barColor="#EDBB4A"
              />
            </Box>
          )}
        </Box>
        {mode == 'USER' && (
          <>
            {isLarge ? (
              <Accordion disableGutters elevation={0} square expanded={expand}>
                <AccordionSummary
                  onClick={toggleAcordion}
                  expandIcon={
                    <Image
                      src={ChevronRight}
                      alt={'Drop_Down_Arrow'}
                      width={12}
                      height={12}
                      style={{
                        transform: 'rotate(90deg)',
                        transition: '0.3s ease-in-out',
                      }}
                      objectFit="cover"
                    />
                  }
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography
                    color={'primary.main'}
                    sx={{p: theme.spacing(0, 0.375, 0, 2)}}
                    variant="subtitle3"
                  >
                    Edit Profile
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    marginTop: 2,
                    p: 1,
                    borderRadius: 2,
                    border: 0.6,
                    borderColor: '#7E7E7E33',
                  }}
                >
                  <LinkSection {...props} />
                </AccordionDetails>
              </Accordion>
            ) : (
              <>
                <Typography
                  color={'primary.main'}
                  sx={{p: theme.spacing(0, 0.375, 0, 2)}}
                  variant="body3"
                  onClick={toggleAcordion}
                >
                  Edit Profile
                </Typography>
              </>
            )}
            <Box position={'relative'} width={'100%'}>
              <Grid
                container
                onClick={event => {
                  event?.preventDefault();
                  openUrl(
                    `${getOldLink('/applied-jobs?ref=profilewdg')}`,
                    '_blank'
                  );
                }}
                data-testid="jobfeed_applied_jobs_container"
                sx={{
                  width: 'auto',
                  m: theme.spacing(1, 2, 1.5, 2),
                  borderRadius: 2,
                  p: 1,
                  border: 0.6,
                  lineHeight: 0,
                  borderColor: 'primary.main',
                  cursor: 'pointer',
                }}
              >
                <Grid item lg={12} data-testid="jobfeed_applied_jobs_count">
                  <Typography
                    component={'p'}
                    color="primary"
                    lineHeight={'16px'}
                    m={0}
                    variant="subtitle2"
                  >
                    {props.appliedJobs}&nbsp;
                  </Typography>
                </Grid>
                <Grid
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    width: '100%',
                  }}
                >
                  <Grid
                    item
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      color="primary"
                      display="flex"
                      lineHeight={'16px'}
                      m={0}
                      variant="subtitle3"
                      sx={{
                        cursor: 'pointer',
                      }}
                    >
                      {' Applied Jobs'}&nbsp;
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: '-2px',
                    }}
                  >
                    <Typography
                      color="secondary"
                      fontWeight={500}
                      fontSize={theme.typography.pxToRem(10)}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      variant="caption"
                    >
                      (last 30 days)
                      <Image
                        src={ChevronRight}
                        alt={'Arrow'}
                        width={10}
                        height={10}
                        objectFit="cover"
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Card>
      {mode == 'USER' && (
        <>
          {!isLarge && (
            <BottomSheet open={expand} onDismiss={toggleAcordion}>
              <Box sx={{px: 1.5, py: 3}}>
                <LinkSection {...props} />
              </Box>
            </BottomSheet>
          )}
        </>
      )}
    </>
  );
};

export default Component;
